import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import * as React from 'react';
import { useLocalization } from '@shared-ui/localization-context';
import { c as classNames, l as liveAnnounce, L as Level } from '../theme-provider/index-chunk.esm.js';
import '../button/index-chunk.esm.js';
import '../icon/index-chunk.esm.js';
import { b as EGDSPagingPrevButton, a as EGDSPagingNextButton } from '../paging-prev-button/index-chunk.esm.js';
import styles from '../styles/uitk-egds-calendar.css';
import { debounce } from 'throttle-debounce';
import { M as MakeEgdsSizingLength } from '../egds-sizing-length/index-chunk.esm.js';
import { c as EGDSText } from '../text/index-chunk.esm.js';
import { i as EGDSLayoutFlex, j as EGDSLayoutFlexItem } from '../layout-flex-item/index-chunk.esm.js';
import { Calendar } from 'calendar-base';
let _locale = 'en-US';
/**
 * Helper for getting year and month from date string.
 * If called with no arguments, uses today's date.
 *
 * @param {String} dateString                Date string in yyyy-mm-dd format
 * @param {Date} date    Fallback date if dateString is falsy. Defaults to today's date.
 * @param {Object} props {month, year} If given, it will be used instead of date and dateString
 * @return {Object} {month, year}
 */
const getMonthYear = (dateString, date, props) => {
  if (typeof props?.month === 'number' && typeof props.year === 'number') {
    return {
      month: props.month,
      year: props.year
    };
  }
  if (dateString) {
    const dateElems = dateString.split('-');
    return {
      month: Number(dateElems[1]) - 1,
      year: Number(dateElems[0])
    };
  }
  if (date) {
    return {
      month: date.getMonth(),
      year: date.getFullYear()
    };
  }
  const today = new Date();
  return {
    month: today.getMonth(),
    year: today.getFullYear()
  };
};
/**
 * Helper for getting string with a language sensitive representation of the date portion of this date.
 *
 * @param {String} dateString   Date string in yyyy-mm-dd format
 * @param {String} locale   Moniker or BCP-47 language tag (fr_FR_HA|fr-FR)
 * @param {Intl.DateTimeFormatOptions} options  Date.prototype.toLocaleDateString() options ( https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString)
 * @return {String} Language sensitive representation of the date
 */
const getLocaleDateString = (dateString, locale, options) => {
  // eslint-disable-next-line no-param-reassign
  locale = locale || _locale;
  if (!dateString) {
    return '';
  }
  if (locale !== _locale) {
    _locale = locale;
  }
  const parts = dateString.split('-');
  const dateObj = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  return dateObj.toLocaleDateString(locale, options);
};
/**
 * Helper for getting language sensitive representation of the date range.
 *
 * @param {String} startDateString   Date string in yyyy-mm-dd format
 * @param {String} endDateString   Date string in yyyy-mm-dd format
 * @param {String} locale   Moniker or BCP-47 language tag (fr_FR_HA|fr-FR)
 * @param {Intl.DateTimeFormatOptions} options  Date.prototype.toLocaleDateString() options ( https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString)
 * @return {String} Language sensitive representation of the dates
 */
const getLocaleConsolidatedDateString = (startDateString, endDateString, locale, options) => {
  if (!startDateString || !endDateString) {
    return getLocaleDateString(startDateString || endDateString, locale, options);
  }
  const startDate = getLocaleDateString(startDateString, locale, options);
  const endDate = getLocaleDateString(endDateString, locale, options);
  // Use "mmm dd - mmm dd" or "mm/dd/yyyy - mm/dd/yyyy"
  return `${startDate} - ${endDate}`;
};
/**
 * Returns string representation of a date when given a date object.
 * The result is in the format "YYYY-MM-DD".
 *
 * @param {Date} date - date object
 * @return {string}  string representation of date.
 */
const getDateToString = date => {
  let month = date.getMonth() + 1;
  month = month < 10 ? `0${month}` : month;
  let day = date.getDate();
  day = day < 10 ? `0${day}` : day;
  return `${date.getFullYear()}-${month}-${day}`;
};
/**
 * Compares 2 date strings and returns an integer that indicates whether the
 * second date is earlier (-1), later (1), or equal to (0) the first.
 *
 * @param {string} startString - start date string in format, "YYYY-MM-DD"
 * @param {string} endString - end date string in format, "YYYY-MM-DD"
 * @return {int}  indication or comparison
 */
const compareDateStrings = (startString = '', endString = '') => {
  if (!startString || !endString) return 0;
  if (startString === endString) {
    return 0;
  }
  const difference = Date.parse(endString) - Date.parse(startString);
  if (difference > 0) {
    return 1;
  }
  if (difference < 0) {
    return -1;
  }
  return 0;
};
const isSameDate = (startString, endString) => startString && endString ? startString === endString : false;
const isEarlierDate = (startString, endString) => startString && endString ? compareDateStrings(startString, endString) === -1 : false;
const isLaterDate = (startString, endString) => startString && endString ? compareDateStrings(startString, endString) === 1 : false;
/**
 * Returns the formatted version of the YYYY-MM-DD date string.
 *
 * @param {String} dateString    Date string in yyyy-mm-dd format
 * @param {String} locale        Moniker or BCP-47 language tag (fr_FR_HA|fr-FR)
 * @param {Intl.DateTimeFormatOptions} options       Date.prototype.toLocaleDateString() options (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString)
 * @returns {String}             Language sensitive representation of the date OR override
 */
const getEffectiveDateText = (dateString, locale, options) => getLocaleDateString(dateString, locale, options);
/**
 * Helper for getting language sensitive representation of the date range
 *
 * @param startDate         Start date string in yyyy-mm-dd format
 * @param endDate           End date string in yyyy-mm-dd format
 * @param locale            Moniker or BCP-47 language tag (fr_FR_HA|fr-FR)
 * @param {Intl.DateTimeFormatOptions} options           Date.prototype.toLocaleDateString() options (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString)
 * @returns {String|string}  Language sensitive representation of the dates with overrides if needed
 */
const getEffectiveConsolidatedDateString = (startDate, endDate, locale, options) => {
  if (startDate || endDate) {
    const effectiveStartDate = getEffectiveDateText(startDate, locale, options);
    const effectiveEndDate = getEffectiveDateText(endDate, locale, options);
    return `${effectiveStartDate} - ${effectiveEndDate}`;
  }
  return getLocaleConsolidatedDateString(startDate, endDate, locale, options);
};
var dateUtils$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  compareDateStrings: compareDateStrings,
  getDateToString: getDateToString,
  getEffectiveConsolidatedDateString: getEffectiveConsolidatedDateString,
  getEffectiveDateText: getEffectiveDateText,
  getLocaleConsolidatedDateString: getLocaleConsolidatedDateString,
  getLocaleDateString: getLocaleDateString,
  getMonthYear: getMonthYear,
  isEarlierDate: isEarlierDate,
  isLaterDate: isLaterDate,
  isSameDate: isSameDate
});
const getClassNameList$c = classNames.bind(styles);
const edgeSelectionClasses = {
  StartAndEndSameDayEdge: 'uitk-calendar-day-selection-edge-same-day',
  StartRangeEdge: 'uitk-calendar-day-selection-edge-start',
  SingleDate: 'uitk-calendar-day-selection-edge-start',
  EndRangeEdge: 'uitk-calendar-day-selection-edge-end',
  UnconfirmedEndRangeEdge: 'uitk-calendar-day-selection-edge-unconfirmed-end',
  UnconfirmedStartAndEndSameDayEdge: 'uitk-calendar-day-selection-edge-unconfirmed-same-day'
};
const getEdgeSelectorClass = selectionType => edgeSelectionClasses[selectionType] || '';
const DaySelectionEdge = props => {
  const {
    selectionType,
    children
  } = props;
  const classes = React.useMemo(() => getClassNameList$c(getEdgeSelectorClass(selectionType)), [selectionType]);
  return classes ? jsx("div", {
    className: classes,
    children: children
  }) : jsx(Fragment, {
    children: children
  });
};
const getClassNameList$b = classNames.bind(styles);
const inRangeEdgeCaseClasses = {
  EndRangeEdge: 'uitk-calendar-day-selection-range-end',
  UnconfirmedEndRangeEdge: 'uitk-calendar-day-selection-range-end',
  SingleDate: 'uitk-calendar-day-selection-range-start',
  StartRangeEdge: 'uitk-calendar-day-selection-range-start',
  UnconfirmedStartAndEndSameDayEdge: 'uitk-calendar-day-selection-range-start',
  WithinRangeFirstOfMonth: 'uitk-calendar-day-selection-in-range-first-of-month',
  WithinRangeLastOfMonth: 'uitk-calendar-day-selection-in-range-last-of-month'
};
const getInRangeBackgroundClass = selectionType => inRangeEdgeCaseClasses[selectionType] || 'uitk-calendar-day-selection-in-range';
const DaySelection = props => {
  const {
    children = null,
    selectionType
  } = props;
  const inRangeBackgroundClass = React.useMemo(() => getClassNameList$b('uitk-calendar-day-selection', getInRangeBackgroundClass(selectionType)), [selectionType]);
  if (selectionType === 'Unselected') {
    return children ? jsx(Fragment, {
      children: children
    }) : null;
  }
  const daySelectionProps = {
    className: inRangeBackgroundClass
  };
  return jsx("div", {
    ...daySelectionProps,
    children: jsx(DaySelectionEdge, {
      selectionType: selectionType,
      children: children
    })
  });
};
const getClassNameList$a = classNames.bind(styles);
const preventFocusOnEmptyClick = e => {
  e.preventDefault();
  e.stopPropagation();
};
const EmptyDay = ({
  className
}) => jsx("td", {
  onMouseDown: preventFocusOnEmptyClick,
  className: className,
  tabIndex: -1,
  "aria-hidden": true
});
const EGDSDay = React.memo(props => {
  const {
    ariaLabel,
    customAriaLabels,
    day,
    dayData,
    dayTemplateComponent: DayTemplateComponent,
    enablePastDates,
    handleArrowKeyPress,
    isFocusable,
    isFocused,
    l10n,
    minHeight,
    onDayClick,
    onDayHover,
    onDisabledDayClick,
    preventScrollOnDayFocus,
    selectable,
    selectionType,
    stretchy = false
  } = props;
  const dayCellRef = React.useRef(null);
  React.useEffect(() => {
    if (isFocused) {
      dayCellRef.current?.focus({
        preventScroll: preventScrollOnDayFocus?.current
      });
      if (preventScrollOnDayFocus?.current) {
        preventScrollOnDayFocus.current = false;
      }
    }
  }, [isFocused, dayCellRef, preventScrollOnDayFocus]);
  const canClick = React.useMemo(() => DayTemplateComponent.canClick(dayData, day, enablePastDates), [day, dayData, DayTemplateComponent, enablePastDates]);
  const canSpan = React.useMemo(() => DayTemplateComponent.canSpan(dayData, day, enablePastDates), [day, dayData, DayTemplateComponent, enablePastDates]);
  const handleDayClick = React.useCallback(event => {
    if (event) {
      event.stopPropagation();
    }
    if (canClick) {
      onDayClick(day);
    } else if (onDisabledDayClick) {
      onDisabledDayClick(day.dateString);
    }
  }, [canClick, day, onDayClick, onDisabledDayClick]);
  const handleDayKeyboardEvents = React.useCallback(e => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleDayClick(e);
      // Prevent scroll on space press
      e.preventDefault();
    } else {
      handleArrowKeyPress(e, day);
    }
  }, [day, handleArrowKeyPress, handleDayClick]);
  const handleDayHover = React.useCallback(() => {
    if (canSpan) {
      onDayHover(day);
    }
  }, [canSpan, day, onDayHover]);
  const {
    isPast,
    isToday,
    isPastMaxDate
  } = day || {};
  const dayClasses = React.useMemo(() => getClassNameList$a('uitk-day', {
    'uitk-day-past': isPast,
    'uitk-day-today': isToday,
    'uitk-day-past-max-date': isPastMaxDate,
    'uitk-day-stretchy': stretchy,
    'uitk-day-has-min-height': minHeight
  }), [isPast, isToday, isPastMaxDate]);
  const dayButtonClass = React.useMemo(() => getClassNameList$a('uitk-day-button', {
    'uitk-day-selectable': selectable && canSpan,
    'uitk-day-clickable': selectable && canClick,
    'uitk-day-selection-same-day': selectionType === 'StartAndEndSameDayEdge'
  }), [canClick, canSpan, selectable, selectionType]);
  const isDayEmpty = !day;
  const dateAriaLabel = React.useMemo(() => isDayEmpty ? '' : ariaLabel || DayTemplateComponent.getAriaLabel(dayData, day, l10n, enablePastDates), [DayTemplateComponent, ariaLabel, day, dayData, isDayEmpty, l10n, enablePastDates]);
  let selectedStateLabel;
  if (selectionType === 'StartRangeEdge' || selectionType === 'UnconfirmedStartAndEndSameDayEdge') selectedStateLabel = customAriaLabels?.startDateSelected || l10n.formatText('calendar.selected.startDate');else if (selectionType === 'EndRangeEdge') selectedStateLabel = customAriaLabels?.endDateSelected || l10n.formatText('calendar.selected.endDate');else if (selectionType === 'SingleDate') selectedStateLabel = l10n.formatText('calendar.selected.singleDate');else if (selectionType === 'StartAndEndSameDayEdge') selectedStateLabel = customAriaLabels?.startAndEndDateSelected || l10n.formatText('calendar.selected.startAndEndDate');
  const dayAriaLabel = selectedStateLabel ? [dateAriaLabel, selectedStateLabel].join(', ') : dateAriaLabel;
  if (isDayEmpty) {
    const emptyDayProps = {
      className: getClassNameList$a({
        'uitk-day': true,
        'uitk-day-stretchy': stretchy,
        'uitk-day-has-min-height': minHeight
      })
    };
    return jsx(EmptyDay, {
      ...emptyDayProps
    });
  }
  const tabIndex = selectionType === 'StartRangeEdge' || selectionType === 'SingleDate' || selectionType === 'StartAndEndSameDayEdge' || selectionType === 'UnconfirmedStartAndEndSameDayEdge' || isFocusable ? 0 : -1;
  const cssCustomProps = {
    ...(minHeight && {
      '--uitk-day-min-cell-height': `${MakeEgdsSizingLength(minHeight)}`
    })
  };
  return jsx("td", {
    className: dayClasses,
    style: cssCustomProps,
    children: jsxs("div", {
      className: dayButtonClass,
      role: "button",
      ref: dayCellRef,
      tabIndex: tabIndex,
      onKeyDown: handleDayKeyboardEvents,
      onClick: handleDayClick,
      onMouseEnter: handleDayHover,
      "aria-disabled": !canClick,
      "aria-pressed": selectionType !== 'Unselected',
      children: [jsx("div", {
        className: "uitk-day-aria-label",
        "aria-label": dayAriaLabel
      }), jsx(DaySelection, {
        selectionType: selectionType,
        children: jsx(DayTemplateComponent, {
          day: day,
          dayData: dayData,
          enablePastDates: enablePastDates
        })
      })]
    })
  });
});
EGDSDay.displayName = 'EGDSDay';

/**
 * Returns a date object when given a string, "YYYY-MM-DD".
 *
 * new Date("YYYY-MM-DD") and new Date(YYYY,MM,DD) produce different results.
 *
 * When using an ISO string, missing HH:mm:ss are initialized to 0, but the date
 * gets converted to the user's timezone:
 * https://tc39.es/ecma262/multipage/numbers-and-dates.html#sec-date.parse
 *
 * "When the HH, mm, or ss elements are absent, "00" is used.
 * When the sss element is absent, "000" is used.
 * When the UTC offset representation is absent, date-only forms are interpreted as a UTC time and date-time forms
 * are interpreted as a local time."
 *
 * @param {string} dateString - date string in the format "YYYY-MM-DD"
 * @return {Date}  New Date object from provided string.
 */
const getDateFromString = dateString => {
  const dateElems = dateString.split('-');
  return new Date(Number(dateElems[0]), Number(dateElems[1]) - 1, Number(dateElems[2]));
};
/**
 * Returns string representation of a date when given a custom
 * object with year, month, day. That custom object is provided
 * by the calendar-base data.
 * The result is in the format "YYYY-MM-DD".
 *
 * @param {object} obj - custom date object
 * @return {string}  string representation of date.
 */
const getDayToString = obj => {
  // eslint-disable-next-line prefer-const
  let {
    year,
    month,
    day
  } = obj;
  month += 1;
  month = month < 10 ? `0${month}` : month;
  day = day < 10 ? `0${day}` : day;
  return `${year}-${month}-${day}`;
};
/**
 * Returns a javascript Date object given a custom Day object provided
 * by the calendar-base data.
 *
 * @param {object} obj - custom date object
 * @return {Date} javascript Date object
 */
const getDayToDate = obj => {
  const {
    year,
    month,
    day
  } = obj;
  return new Date(year, month, day);
};
/**
 * Returns an array of date objects given a start date and a number
 * of nights.
 *
 * @param {string} start - start date in format, "YYYY-MM-DD"
 * @param {number} num - number of nights
 * @return {array}  Array of date objects
 */
const getDateRange = (start, num) => {
  const startDate = getDateFromString(start);
  const dates = [getDateToString(new Date(startDate))];
  for (let i = 0; i < num; i++) {
    dates.push(getDateToString(new Date(startDate.setDate(startDate.getDate() + 1))));
  }
  return dates;
};
/**
 * Returns the number of days between two date strings.
 *
 * @param {string} startString - start date string in format, "YYYY-MM-DD"
 * @param {string} endString - end date string in format, "YYYY-MM-DD"
 * @return {number}  number of days
 */
const getNumDaysBetweenDateStrings = (startString, endString) => {
  let numDays = 0;
  const startDate = getDateFromString(startString);
  const endDate = getDateFromString(endString);
  // eslint-disable-next-line no-unmodified-loop-condition
  while (endDate > startDate) {
    // eslint-disable-next-line no-plusplus
    numDays++;
    startDate.setDate(startDate.getDate() + 1);
  }
  return numDays;
};
/**
 * Returns whether the date represented by the given custom date object is the last day of the given month.
 *
 * @param day {object} The custom day object to check
 * @returns {boolean} true if it is the last day of the given month, otherwise false
 */
const isLastDayInMonth = day => {
  const test = getDayToDate(day);
  if (test.getDate() < 28) {
    return false;
  }
  test.setDate(test.getDate() + 1);
  return test.getDate() === 1;
};
/**
 * Returns whether the date represented by the given custom date object is the first day of the given month.
 *
 * @param day {object} The custom day object to check
 * @returns {boolean} true if it is the first day of the given month, otherwise false
 */
const isFirstDayInMonth = day => {
  const test = getDayToDate(day);
  if (test.getDate() !== 1) {
    return false;
  }
  return true;
};
/**
 * Given a month, add a number of months to it and return the new month.
 *
 * @param year {object} Year of the month
 * @param month {object} month number (zero-based)
 * @param num {object} Number of months to add
 * @returns {object} object with the year and month of the new month
 */
const addMonthsToMonth = (year, month, num) => {
  const date = getDayToDate({
    year,
    month,
    day: '01'
  });
  date.setMonth(date.getMonth() + num);
  return {
    updatedYear: date.getFullYear(),
    updatedMonth: date.getMonth()
  };
};
/**
 * From an array of days, both white spaces(false) or days, it groups them in weeks
 *
 * @param monthDays
 * @returns An array of weeks, each one is an array of days.
 */
const groupMonthDaysInWeeks = monthDays => {
  const weeks = [];
  for (let i = 0; i < monthDays.length; i += daysInWeek) {
    weeks.push(monthDays.slice(i, i + daysInWeek));
  }
  return weeks;
};
var dateUtils = {
  getDateFromString,
  getDayToString,
  getDayToDate,
  getDateRange,
  getNumDaysBetweenDateStrings,
  isFirstDayInMonth,
  isLastDayInMonth,
  addMonthsToMonth,
  groupMonthDaysInWeeks
};
const daysInWeek = 7;
const defaultLocale = 'en-US';
const weekdayIndexes = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6
};
const capitalizeFirstLetter = ([firstChar, ...rest], locale) => {
  const capitalizedFirstLetter = firstChar.toLocaleUpperCase(locale);
  return [capitalizedFirstLetter, ...rest].join('');
};
const getIntDateTimeFormat = (locale, options) => {
  try {
    return new Intl.DateTimeFormat(locale, options);
  } catch (e) {
    return new Intl.DateTimeFormat(defaultLocale, options);
  }
};
const getWeekStartIndex = l10n => {
  const firstWeekdayCode = l10n.getWeekData().firstDay || 'sun';
  const firstWeekdayIndex = weekdayIndexes[firstWeekdayCode];
  return firstWeekdayIndex;
};
const getWeekdayNames = (l10n, locale = defaultLocale, weekday = 'long') => {
  const firstWeekdayIndex = getWeekStartIndex(l10n);
  const {
    format
  } = getIntDateTimeFormat(locale, {
    weekday
  });
  return Array.from(Array(daysInWeek).keys()).map(weekdayIndex => {
    // WeekIndex is zero based.
    const day = weekdayIndex + 1 + firstWeekdayIndex;
    // 2022-5-1 is a Sunday date.
    const weekDayName = format(new Date(2022, 4, day));
    // In some locales, days are capitalized shown as the first word in a phrase.
    return ['pt-BR', 'da-DK', 'nl-NL', 'nb-NO', 'no-NO', 'sv-SE'].includes(locale) ? capitalizeFirstLetter(weekDayName, locale) : weekDayName;
  });
};
const getMonthTitle = (year, month, locale = defaultLocale, monthLength = 'long') => {
  const {
    format
  } = getIntDateTimeFormat(locale, {
    year: 'numeric',
    month: monthLength
  });
  // MonthIndex is zero based.
  const monthLabel = format(new Date(year, month, 3));
  // In some locales, months are capitalized as the first word in a phrase.
  return ['da-DK'].includes(locale) ? capitalizeFirstLetter(monthLabel, locale) : monthLabel;
};
const getAriaLiveDate = (ISODateString, locale) => {
  if (!ISODateString) {
    return '';
  }
  const {
    format
  } = getIntDateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long'
  });
  try {
    const date = dateUtils.getDateFromString(ISODateString);
    return format(date);
  } catch (e) {
    return '';
  }
};
const getClassNameList$9 = classNames.bind(styles);
const WeekdayNames = ({
  dayNameLength,
  locale
}) => {
  const baseClass = 'uitk-weekday';
  const weekDayNameClasses = React.useMemo(() => getClassNameList$9(`${baseClass}-names`), []);
  const weekDayClasses = React.useMemo(() => getClassNameList$9(`${baseClass}-title`), []);
  const l10n = useLocalization();
  const longWeekdayNames = React.useMemo(() => getWeekdayNames(l10n, locale, 'long'), [l10n, locale]);
  const weekdayNames = React.useMemo(() => dayNameLength === 'long' ? longWeekdayNames : getWeekdayNames(l10n, locale, dayNameLength), [dayNameLength, l10n, locale, longWeekdayNames]);
  return jsx("tr", {
    className: weekDayNameClasses,
    children: weekdayNames.map((weekdayName, i) => jsxs("th", {
      scope: "col",
      className: weekDayClasses,
      children: [jsx("span", {
        "aria-hidden": "true",
        children: weekdayName
      }), jsx("span", {
        className: "is-visually-hidden",
        children: longWeekdayNames[i]
      })]
    }, `weekday-${i}`))
  });
};
WeekdayNames.displayName = 'WeekdayNames';
const isDateWithinRange = (selectionStart, selectionEnd, date) => !!selectionStart && !!selectionEnd && isLaterDate(selectionStart, date) && isEarlierDate(selectionEnd, date);
const getSelectionType = ({
  selectionStart,
  selectionEnd,
  isSingleDateSelection,
  day,
  enableSameDayDateRange,
  unconfirmedSelectionEnd
}) => {
  const dateString = day ? day.dateString : '';
  if (!dateString || !selectionStart && !selectionEnd && !unconfirmedSelectionEnd && !isSingleDateSelection || isSingleDateSelection && !selectionStart) {
    return 'Unselected';
  }
  if (isSingleDateSelection && isSameDate(selectionStart, dateString)) {
    return 'SingleDate';
  }
  if (enableSameDayDateRange && isSameDate(selectionStart, selectionEnd) && isSameDate(selectionStart, dateString)) {
    return 'StartAndEndSameDayEdge';
  }
  if (enableSameDayDateRange && isSameDate(selectionStart, unconfirmedSelectionEnd) && isSameDate(unconfirmedSelectionEnd, dateString)) {
    return 'UnconfirmedStartAndEndSameDayEdge';
  }
  if (isSameDate(selectionStart, dateString)) {
    return 'StartRangeEdge';
  }
  if (isSameDate(selectionEnd, dateString)) {
    return 'EndRangeEdge';
  }
  if (isSameDate(unconfirmedSelectionEnd, dateString)) {
    if (selectionStart && unconfirmedSelectionEnd && isEarlierDate(selectionStart, unconfirmedSelectionEnd)) {
      return 'Unselected';
    }
    return 'UnconfirmedEndRangeEdge';
  }
  if (!isSingleDateSelection && isDateWithinRange(selectionStart, selectionEnd || unconfirmedSelectionEnd, dateString)) {
    if (dateUtils.isLastDayInMonth(day)) {
      return 'WithinRangeLastOfMonth';
    }
    if (dateUtils.isFirstDayInMonth(day)) {
      return 'WithinRangeFirstOfMonth';
    }
    return 'WithinRange';
  }
  return 'Unselected';
};
const getClassNameList$8 = classNames.bind(styles);
const MonthTitle = ({
  title,
  monthLabelAlignment
}) => {
  const labelClasses = getClassNameList$8(`uitk-align-${monthLabelAlignment}`, 'uitk-month-label');
  return title ? jsx("span", {
    className: labelClasses,
    children: title
  }) : null;
};
const isCurrentMonthAndYear = (month, year) => {
  const today = new Date();
  return month === today.getMonth() && year === today.getFullYear();
};
const EGDSMonth = props => {
  const {
    customAriaLabels,
    dayData,
    dayNameLength,
    dayTemplateComponent,
    enablePastDates,
    enableSameDayDateRange,
    focusedDay,
    handleArrowKeyPress,
    index,
    isFirstMonth,
    isLastMonth,
    isSecondMonth,
    isSingleDateSelection,
    l10n,
    locale,
    minDayCellHeight,
    monthData: {
      year,
      month,
      weeks
    },
    monthLabelAlignment,
    numColumns,
    numMonthsToDisplay,
    onDayClick,
    onDayHover,
    onDisabledDayClick,
    onScrollHandler,
    preventScrollOnDayFocus,
    scrollToMonthRef,
    selectable,
    selectionEnd,
    selectionStart,
    firstMonthCallback,
    lastMonthCallback,
    secondMonthCallback,
    shouldScrollIntoView,
    showMonthLabels,
    stretchy = false,
    unconfirmedSelectionEnd
  } = props;
  /**
   * Determines whether a day should be focusable. If there is not a day selected then a focusable day is either
   * the current day of the present month or the first day of the month as long as it is not in the past
   *
   * @param day {object} The custom day object to check
   * @param i {number} index of month
   * @returns {boolean} true if day is the focusable day of the given month, false otherwise
   */
  const firstFocusableDay = (day, i, isCurrentMonth, tableRow) => {
    if (!day) {
      return false;
    }
    const {
      month: dayMonth,
      year: dayYear,
      isToday,
      isPast,
      isPastMaxDate
    } = day;
    const startDate = selectionStart ? dateUtils.getDateFromString(selectionStart) : null;
    let outOfRange = true;
    let monthDiff;
    let yearDiff;
    if (numMonthsToDisplay && startDate !== null) {
      yearDiff = dayYear - startDate.getFullYear();
      monthDiff = Math.abs(dayMonth + yearDiff * 12 - startDate.getMonth());
      outOfRange = monthDiff >= numMonthsToDisplay;
      if (startDate.getMonth() < dayMonth && startDate.getFullYear() <= dayYear) {
        outOfRange = true;
      }
    }
    if (outOfRange) {
      if (!isPast && !isPastMaxDate && (i === 0 || isCurrentMonth)) {
        // Focusable day must be within the current date's month
        if (isToday) {
          return true;
        }
        if (dateUtils.isFirstDayInMonth(day) && tableRow === 0) {
          // Verifying the table row is 0 prevents potential sibling months from also receiving focus on their first day
          return true;
        }
      }
    }
    return false;
  };
  const flexBasis = `${100 / numColumns}%`;
  const monthClasses = getClassNameList$8({
    'uitk-month': true,
    'uitk-month-single': numColumns === 1,
    'uitk-month-double': numColumns === 2,
    'uitk-month-double-left': numColumns === 2 && index % 2 === 0,
    'uitk-month-double-right': numColumns === 2 && index % 2 === 1,
    'uitk-month-multi': numColumns > 2,
    'uitk-month-stretchy': stretchy
  });
  const tableClasses = getClassNameList$8({
    'uitk-month-table': true,
    'uitk-month-table-stretchy': stretchy
  });
  const monthTitle = getMonthTitle(year, month, locale, 'long');
  const defaultMonthRef = React.useRef(null);
  const monthRef = shouldScrollIntoView ? scrollToMonthRef : defaultMonthRef;
  const intersectionObserverRef = React.useRef(null);
  React.useEffect(() => {
    if (monthRef?.current) {
      if (isFirstMonth) {
        firstMonthCallback?.(monthRef.current);
      }
      if (isSecondMonth) {
        secondMonthCallback?.(monthRef.current);
      }
      if (isLastMonth) {
        lastMonthCallback?.(monthRef.current);
      }
    }
  }, [isFirstMonth, isLastMonth, isSecondMonth, monthRef, firstMonthCallback, lastMonthCallback, secondMonthCallback]);
  const firstDateInMonth = React.useMemo(() => new Date(year, month, 1, 12), [year, month]);
  const today = React.useMemo(() => new Date(), []);
  const isTodayInMonth = React.useMemo(() => today.getFullYear() === year && today.getMonth() === month, [today, month, year]);
  const date = React.useMemo(() => isTodayInMonth ? today : firstDateInMonth, [firstDateInMonth, isTodayInMonth, today]);
  React.useEffect(() => {
    if (!onScrollHandler) return;
    if (!intersectionObserverRef.current) {
      intersectionObserverRef.current = new IntersectionObserver(([entry]) => {
        if (entry.intersectionRatio === 1) {
          onScrollHandler(date);
        }
      }, {
        root: null,
        rootMargin: '0px',
        threshold: 1
      });
    }
    if (defaultMonthRef.current && intersectionObserverRef.current) {
      intersectionObserverRef.current.observe(defaultMonthRef.current);
    }
    return () => {
      if (intersectionObserverRef.current && defaultMonthRef?.current) {
        intersectionObserverRef.current.unobserve(defaultMonthRef.current);
        intersectionObserverRef.current = null;
      }
    };
  }, [onScrollHandler, date]);
  const isCurrentMonthYear = React.useMemo(() => isCurrentMonthAndYear(month, year), [month, year]);
  return jsxs("div", {
    style: {
      flexBasis: `${flexBasis}`
    },
    className: monthClasses,
    ref: monthRef,
    children: [showMonthLabels && jsx(MonthTitle, {
      title: monthTitle,
      monthLabelAlignment
    }), jsxs("table", {
      className: tableClasses,
      "data-stid": "month-table",
      role: "presentation",
      children: [jsx("thead", {
        role: "presentation",
        children: jsx(WeekdayNames, {
          dayNameLength: dayNameLength,
          locale: locale
        })
      }), jsx("tbody", {
        className: getClassNameList$8('uitk-day-wrap'),
        children: weeks.map((days, weekIndex) => jsx("tr", {
          children: days.map((day, weekdayIndex) => {
            const selectionType = getSelectionType({
              selectionStart,
              selectionEnd,
              isSingleDateSelection,
              day,
              enableSameDayDateRange,
              unconfirmedSelectionEnd
            });
            const dayProps = {
              customAriaLabels,
              day,
              handleArrowKeyPress,
              selectable,
              onDayClick,
              stretchy,
              minHeight: minDayCellHeight,
              onDayHover,
              dayData: day && dayData?.[day.dateString] || dayData?.defaults || null,
              dayTemplateComponent,
              enablePastDates: enablePastDates ?? false,
              onDisabledDayClick,
              isFocusable: firstFocusableDay(day, index, isCurrentMonthYear, weekIndex),
              isFocused: !!(focusedDay && isSameDate(day.dateString, focusedDay.dateString)),
              l10n,
              preventScrollOnDayFocus,
              selectionType
            };
            return jsx(EGDSDay, {
              ...dayProps
            }, `day-${weekdayIndex}`);
          })
        }, weekIndex))
      })]
    })]
  });
};
EGDSMonth.displayName = 'EGDSMonth';
const dayDataDefault = {
  defaults: {}
};
EGDSMonth.defaultProps = {
  dayData: dayDataDefault,
  padWeeks: false,
  scrollToMonth: false,
  selectionEnd: null,
  selectionStart: null
};
// This code is extracted from shouldComponentUpdate and needs to be revisited
const isObject = obj => obj === Object(obj);
const getMonth = dateString => {
  // Concatenating the year and month 201611, for comparisons
  if (dateString) {
    const parts = dateString.split('-');
    return Number(`${parts[0]}${parts[1]}`);
  }
  return null;
};
const zeroFill = value => {
  const oneBasedValue = value + 1;
  return oneBasedValue < 10 ? `0${oneBasedValue}` : `${oneBasedValue}`;
};
const getRenderingMonth = (year, month) => {
  const zeroFilledMonth = zeroFill(month);
  return Number(`${year}${zeroFilledMonth}`);
};
const shouldComponentUpdateSingle = (prevProps, nextProps) => {
  const {
    monthData: {
      month,
      year
    },
    selectionStart
  } = prevProps;
  const renderingMonth = getRenderingMonth(year, month);
  const selectedMonth = getMonth(nextProps.selectionStart);
  const prevSelectedMonth = getMonth(selectionStart);
  const buffer = nextProps.showSiblingMonths ? 1 : 0;
  // Clear any selections (i.e. render any month with a previous selection)
  if (renderingMonth === prevSelectedMonth) {
    return true;
  }
  // Skip any months outside of the new selection, plus a buffer (if applicable)
  // Using an extra buffer because the early dates from the next month may show in
  // the previous month. Same for next month dates (showSiblingMonths prop)
  if (selectedMonth && (renderingMonth < selectedMonth - buffer || renderingMonth > selectedMonth + buffer)) {
    return false;
  }
  return true;
};
const shouldComponentUpdateRange = (prevProps, nextProps) => {
  const {
    monthData: {
      month,
      year
    },
    selectionStart,
    selectionEnd,
    unconfirmedSelectionEnd
  } = prevProps;
  const renderingMonth = getRenderingMonth(year, month);
  const selectionStartMonth = getMonth(nextProps.selectionStart);
  const selectionEndMonth = getMonth(nextProps.selectionEnd || nextProps.unconfirmedSelectionEnd);
  const outMonth = selectionEndMonth || selectionStartMonth;
  const buffer = nextProps.showSiblingMonths ? 1 : 0;
  // If the new selection, or partial selection contains the month in question, render.
  // Using an extra buffer because the early dates from the next month may show in
  // the previous month. Same for next month dates (showSiblingMonths prop)
  if (renderingMonth >= Number(selectionStartMonth) - buffer && renderingMonth <= Number(outMonth) + buffer) {
    return true;
  }
  const prevSelectionStartMonth = getMonth(selectionStart);
  const prevSelectionEndMonth = getMonth(selectionEnd || (unconfirmedSelectionEnd === nextProps.selectionStart ? null : unconfirmedSelectionEnd));
  const outMonth2 = prevSelectionEndMonth || Number(prevSelectionStartMonth);
  // If an old selection was drawn on this month, rerender to clear
  if (renderingMonth >= Number(prevSelectionStartMonth) && renderingMonth <= outMonth2) {
    return true;
  }
  return false;
};
// The optimizations in shouldComponentUpdate are for improving the responsiveness
// when making selections, particularly when many months are rendered.
const areEqualProps = (prevProps, nextProps) => {
  const propChange = ['monthData', 'showMonthLabels', 'monthLabelAlignment', 'padWeeks', 'showSiblingMonths', 'selectable', 'dayData', 'isSingleDateSelection', 'locale', 'index', 'focusedDay', 'shouldScrollIntoView', 'unconfirmedSelectionEnd'].some(prop => {
    const thisProp = prevProps[prop];
    const nextProp = nextProps[prop];
    if (isObject(thisProp) && isObject(nextProp)) {
      return JSON.stringify(thisProp) !== JSON.stringify(nextProp);
    }
    return thisProp !== nextProp;
  });
  // Always update if any of the above properties or state change
  if (propChange) {
    return false;
  }
  if (nextProps.isSingleDateSelection) {
    return !shouldComponentUpdateSingle(prevProps, nextProps);
  }
  return !shouldComponentUpdateRange(prevProps, nextProps);
};
var EGDSMonth$1 = React.memo(EGDSMonth, areEqualProps);

/* Type theme */
/** ******************* */
const EGDSDateNumberThemeMap = [['default', 'uitk-date-number-default'], ['emphasis', 'uitk-date-number-emphasis'], ['muted', 'uitk-date-number-muted'], ['action', 'uitk-date-number-action'], ['today', 'uitk-date-number-today']];
const EGDSDateNumberThemes = new Map(EGDSDateNumberThemeMap);
/* Type size */
/** ******************* */
const EGDSTypeSizeMap = [[300, 'uitk-type-300'], [400, 'uitk-type-400']];
const EGDSTypeSizes = new Map(EGDSTypeSizeMap);
/* Type weight */
/** ******************* */
const EGDSTypeWeightMap = [['regular', 'uitk-type-regular'], ['bold', 'uitk-type-bold']];
const EGDSTypeWeights = new Map(EGDSTypeWeightMap);
/* Availability Background availability */
/** ******************* */
const EGDSAvailabilityBackgroundAvailabilityMap = [['available', 'uitk-background-available'], ['am-unavailable', 'uitk-background-am-unavailable'], ['pm-unavailable', 'uitk-background-pm-unavailable'], ['unavailable', 'uitk-background-unavailable']];
const EGDSAvailabilityBackgroundAvailabilities = new Map(EGDSAvailabilityBackgroundAvailabilityMap);
const EGDSEventBarThemeMap = [['default', 'uitk-event-bar-default'], ['emphasis', 'uitk-event-bar-emphasis'], ['muted', 'uitk-event-bar-muted'], ['positive', 'uitk-event-bar-positive'], ['negative', 'uitk-event-bar-negative'], ['primary', 'uitk-event-bar-primary']];
const EGDSEventBarThemes = new Map(EGDSEventBarThemeMap);
const EGDSEventBarTypeMap = [['solid', 'uitk-event-bar-type-solid'], ['bordered', 'uitk-event-bar-type-bordered'], ['bordered-muted-fill', 'uitk-event-bar-type-bordered-muted-fill'], ['dashed-border', 'uitk-event-bar-type-dashed-border'], ['dashed-border-muted-fill', 'uitk-event-bar-type-dashed-border-muted-fill'], ['line-fill-narrow', 'uitk-event-bar-type-line-fill-narrow'], ['line-fill-wide', 'uitk-event-bar-type-line-fill-wide']];
const EGDSEventBarTypes = new Map(EGDSEventBarTypeMap);
const getClassNameList$7 = classNames.bind(styles);
/**
 * This is where the description of Date Number will go.
 */
const EGDSDateNumber = props => {
  const {
    theme = 'default',
    size = 300,
    weight = 'regular',
    disabled = false,
    children
  } = props;
  const wrapperClassNames = getClassNameList$7('uitk-date-number', EGDSDateNumberThemes.get(theme), EGDSTypeSizes.get(size), EGDSTypeWeights.get(weight), {
    'is-disabled': disabled
  });
  const l10n = useLocalization();
  const ariaLabel = theme === 'today' ? l10n.formatText('calendar.today.a11yLabel') : null;
  return jsx("div", {
    className: wrapperClassNames,
    "aria-hidden": !ariaLabel,
    ...(ariaLabel && {
      'aria-label': ariaLabel
    }),
    children: children
  });
};
EGDSDateNumber.displayName = 'EGDSDateNumber';
const DefaultDayTemplate = props => {
  const {
    day: {
      isPast,
      isPastMaxDate,
      isToday,
      day: dateDay
    },
    enablePastDates,
    dayData
  } = props;
  const {
    dateText
  } = dayData;
  const getClassNameList = classNames.bind(styles);
  const wrapperClassNames = getClassNameList('uitk-default-day-template', {
    'uitk-default-day-template-has-text': dateText
  });
  const getDateTextComponent = () => jsx(EGDSLayoutFlexItem, {
    children: jsx("div", {
      className: getClassNameList('uitk-default-day-template-text-area'),
      children: dateText.textValue && jsx(EGDSText, {
        size: 100,
        weight: dateText.textWeight,
        theme: dateText.textTheme,
        children: dateText.textValue
      })
    })
  });
  const getTheme = () => {
    if (isToday) {
      return 'today';
    }
    if (isPast && !enablePastDates || isPastMaxDate) {
      return 'muted';
    }
    return 'default';
  };
  return jsxs(EGDSLayoutFlex, {
    justifyContent: "center",
    alignItems: "center",
    direction: "column",
    blockSize: "full_size",
    className: wrapperClassNames,
    children: [jsx(EGDSLayoutFlexItem, {
      children: jsx(EGDSDateNumber, {
        theme: getTheme(),
        disabled: isPast && !enablePastDates || isPastMaxDate,
        children: dateDay
      })
    }), dateText && getDateTextComponent()]
  });
};
DefaultDayTemplate.canClick = (_dayData, day, enablePastDates) => !day.isPast && !day.isPastMaxDate || enablePastDates && !day.isPastMaxDate;
DefaultDayTemplate.canSpan = (_dayData, day, enablePastDates) => !day.isPast && !day.isPastMaxDate || enablePastDates && !day.isPastMaxDate;
DefaultDayTemplate.getAriaLabel = (_dayData, day, l10n, enablePastDates) => {
  const {
    isPast,
    isPastMaxDate,
    localeDateString
  } = day;
  return isPast && !enablePastDates || isPastMaxDate ? l10n.formatText('calendar.unavailable.a11yLabel', localeDateString) : localeDateString;
};
const AriaLive = ({
  customAriaLabels,
  selectionStart,
  selectionEnd,
  locale
}) => {
  const previousSelectionStartRef = React.useRef(selectionStart);
  const previousSelectionStart = previousSelectionStartRef.current;
  const previousSelectionEndRef = React.useRef(selectionEnd);
  const previousSelectionEnd = previousSelectionEndRef.current;
  const l10n = useLocalization();
  const ariaLiveScenario = React.useMemo(() => {
    const selectionStartHasChanged = selectionStart && previousSelectionStart !== selectionStart;
    if (selectionStartHasChanged) {
      return {
        state: 'NoChange',
        updateSelectionStart: true,
        date: selectionStart
      };
    }
    const selectionEndHasChanged = selectionEnd && previousSelectionEnd !== selectionEnd;
    if (selectionEndHasChanged) {
      return {
        state: 'NoChange',
        updateSelectionEnd: true,
        date: selectionEnd
      };
    }
    const areBothSelectionItemsCleared = previousSelectionStart && previousSelectionEnd && !selectionStart && !selectionEnd;
    if (areBothSelectionItemsCleared) {
      return {
        state: 'BothDatesAreCleared',
        updateSelectionStart: true,
        updateSelectionEnd: true,
        date: selectionStart
      };
    }
    const isStartSelectionCleared = previousSelectionStart && !selectionStart;
    if (isStartSelectionCleared) {
      return {
        state: 'StartDateCleared',
        updateSelectionStart: true,
        date: selectionStart
      };
    }
    const isEndSelectionCleared = previousSelectionEnd && !selectionEnd;
    if (isEndSelectionCleared) {
      return {
        state: 'EndDateCleared',
        updateSelectionEnd: true,
        date: selectionEnd
      };
    }
    return {
      state: 'NoChange'
    };
  }, [previousSelectionStart, selectionStart, previousSelectionEnd, selectionEnd]);
  if (ariaLiveScenario.updateSelectionStart) {
    previousSelectionStartRef.current = selectionStart;
  }
  if (ariaLiveScenario.updateSelectionEnd) {
    previousSelectionEndRef.current = selectionEnd;
  }
  const translationDate = getAriaLiveDate(ariaLiveScenario.date || null, locale);
  const translationKeyMap = {
    StartDateCleared: customAriaLabels?.startDateCleared || l10n.formatText('calendar.startDateCleared.a11yLabel', translationDate),
    EndDateCleared: customAriaLabels?.endDateCleared || l10n.formatText('calendar.endDateCleared.a11yLabel', translationDate),
    BothDatesAreCleared: customAriaLabels?.bothDatesCleared || l10n.formatText('calendar.datesCleared.a11yLabel', translationDate),
    NoChange: ''
  };
  const ariaLiveLabel = translationKeyMap[ariaLiveScenario.state];
  React.useEffect(() => {
    if (ariaLiveLabel) {
      liveAnnounce(ariaLiveLabel, Level.POLITE);
    }
  }, [ariaLiveLabel]);
  return null;
};
var AriaLive$1 = React.memo(AriaLive);
const getToday = () => {
  const date = new Date();
  return {
    day: date.getDate(),
    month: date.getMonth(),
    year: date.getFullYear()
  };
};
const getMaxDate = maxDate => {
  if (maxDate) {
    return {
      day: maxDate.getDate(),
      month: maxDate.getMonth(),
      year: maxDate.getFullYear()
    };
  }
  return undefined;
};
const isToday = (day, today) => day.day === today.day && day.month === today.month && day.year === today.year;
const isPast = (day, today) => compareDateStrings(dateUtils.getDayToString(day), dateUtils.getDayToString(today)) > 0;
const isAfter = (day, today) => compareDateStrings(dateUtils.getDayToString(day), dateUtils.getDayToString(today)) < 0;
var todayUtil = {
  getToday,
  getMaxDate,
  isToday,
  isPast,
  isAfter
};
const sixWeeks = 6;
const sixWeeksInDays = sixWeeks * daysInWeek;
const useCalendarDays = ({
  numMonthsToDisplay,
  year,
  month,
  showSiblingMonths: siblingMonths,
  locale,
  maxDate,
  padWeeks
}) => {
  const l10n = useLocalization();
  const today = React.useMemo(() => todayUtil.getToday(), []);
  const maxDateDay = React.useMemo(() => todayUtil.getMaxDate(maxDate), [maxDate]);
  const calendarData = React.useMemo(() => {
    const weekStart = getWeekStartIndex(l10n);
    return new Calendar({
      siblingMonths,
      weekStart
    });
  }, [l10n, siblingMonths]);
  const daysByWeekAndMonth = React.useMemo(() => {
    const monthIndexes = Array.from(Array(numMonthsToDisplay).keys());
    return monthIndexes.map(monthIndex => {
      const {
        updatedMonth,
        updatedYear
      } = dateUtils.addMonthsToMonth(year, month, monthIndex);
      let monthDays = calendarData.getCalendar(updatedYear, updatedMonth).map(day => day ? {
        ...day,
        dateString: dateUtils.getDayToString(day),
        localeDateString: new Date(Date.UTC(day.year, day.month, day.day)).toLocaleDateString(locale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: 'UTC',
          weekday: 'long'
        }),
        isToday: todayUtil.isToday(day, today),
        isPast: todayUtil.isPast(day, today),
        isPastMaxDate: maxDateDay ? todayUtil.isAfter(day, maxDateDay) : false,
        position: {
          monthIndex
        }
      } : false);
      // This will make all months to have 6 weeks
      if (padWeeks) {
        const extraDaysToCompleteSixWeeks = sixWeeksInDays - monthDays.length;
        const extraDays = Array.from(Array(extraDaysToCompleteSixWeeks)).map(() => false);
        monthDays = monthDays.concat(extraDays);
      }
      const weeksWithPosition = dateUtils.groupMonthDaysInWeeks(monthDays).map((week, weekIndex) => week.map((day, dayIndex) => day ? {
        ...day,
        position: {
          ...day.position,
          weekIndex,
          dayIndex
        }
      } : day));
      return {
        weeks: weeksWithPosition,
        month: updatedMonth,
        year: updatedYear
      };
    });
  }, [calendarData, locale, maxDateDay, month, numMonthsToDisplay, today, year, padWeeks]);
  return daysByWeekAndMonth;
};
const Move = {
  Backward: -1,
  Neutral: 0,
  Forward: 1
};
const moveMap = {
  ArrowRight: {
    x: Move.Forward,
    y: Move.Neutral
  },
  ArrowLeft: {
    x: Move.Backward,
    y: Move.Neutral
  },
  ArrowUp: {
    x: Move.Neutral,
    y: Move.Backward
  },
  ArrowDown: {
    x: Move.Neutral,
    y: Move.Forward
  }
};
const initialState = {
  focusedDay: null,
  isLimitReached: false,
  pendingMoveKey: null,
  prevFocusedDayOutOfBounds: false
};
const firstWeekIndexInMonth = 0;
const firstMonthIndex = 0;
const firstDayIndexInWeek = 0;
const getNewFocusDayPosition = (focusedDay, pendingMoveKey, monthsData) => {
  switch (pendingMoveKey) {
    case 'ArrowRight':
      {
        return {
          weekIndex: firstWeekIndexInMonth,
          monthIndex: firstMonthIndex,
          dayIndex: firstDayIndexInWeek
        };
      }
    case 'ArrowLeft':
      {
        const lastMonthIndex = monthsData.length - 1;
        const lastWeekIndex = monthsData[lastMonthIndex].weeks.length - 1;
        const lastDayInWeek = monthsData[lastMonthIndex].weeks[lastWeekIndex].length - 1;
        return {
          weekIndex: lastWeekIndex,
          monthIndex: lastMonthIndex,
          dayIndex: lastDayInWeek
        };
      }
    case 'ArrowDown':
      {
        return {
          weekIndex: firstDayIndexInWeek,
          monthIndex: firstMonthIndex,
          dayIndex: focusedDay.position.dayIndex
        };
      }
    case 'ArrowUp':
      {
        const lastMonthIndex = monthsData.length - 1;
        return {
          weekIndex: monthsData[lastMonthIndex].weeks.length - 1,
          dayIndex: focusedDay.position.dayIndex,
          monthIndex: lastMonthIndex
        };
      }
    default:
      {
        return {
          weekIndex: firstDayIndexInWeek,
          monthIndex: firstMonthIndex,
          dayIndex: firstDayIndexInWeek
        };
      }
  }
};
const getNormalizedValue = (value, maxValue) => ({
  value: (maxValue + value) % maxValue,
  overflowCarryover: Math.floor(value / maxValue)
});
const movePositionInAxis = (value, maxIndexPosition) => {
  const {
    value: nextValue,
    overflowCarryover
  } = getNormalizedValue(value, maxIndexPosition);
  return {
    value: nextValue,
    overflowCarryover
  };
};
const moveDayCursorHorizontally = (currentPosition, moveOperation, monthsData) => {
  const maxDaysInWeek = monthsData[currentPosition.monthIndex].weeks[currentPosition.weekIndex].length;
  return movePositionInAxis(currentPosition.dayIndex + moveOperation.x, maxDaysInWeek);
};
const moveDayCursorVertically = (currentPosition, moveOperation, yOverflow, monthsData) => {
  const maxWeeksInMonth = monthsData[currentPosition.monthIndex].weeks.length;
  return movePositionInAxis(currentPosition.weekIndex + moveOperation.y + yOverflow, maxWeeksInMonth);
};
const moveCursorPosition = (dayPosition, moveOperation, monthsData) => {
  const {
    value: xPosition,
    overflowCarryover: yOverflow
  } = moveDayCursorHorizontally(dayPosition, moveOperation, monthsData);
  const {
    value: yPosition,
    overflowCarryover: monthIndexOverflow
  } = moveDayCursorVertically(dayPosition, moveOperation, yOverflow, monthsData);
  const {
    monthIndex
  } = dayPosition;
  const newMonthIndex = monthIndex + monthIndexOverflow;
  const numberOfAvailableMonths = monthsData.length;
  // Return null position if the move overflows the existing months
  if (newMonthIndex < 0 || newMonthIndex >= numberOfAvailableMonths) {
    return null;
  }
  // Normalize Y position if it needs to jump to prev/next existing month
  let normalizedYPosition = yPosition;
  if (monthIndex !== newMonthIndex) {
    normalizedYPosition = newMonthIndex < monthIndex ? monthsData[newMonthIndex].weeks.length - 1 : firstWeekIndexInMonth;
  }
  return {
    monthIndex: newMonthIndex,
    weekIndex: normalizedYPosition,
    dayIndex: xPosition
  };
};
const moveCursor = (focusedDayPosition, moveActionKey, monthsData, lastFocusedDay) => {
  const moveOperation = moveMap[moveActionKey];
  const newFocusedPosition = moveCursorPosition(focusedDayPosition, moveOperation, monthsData);
  if (!newFocusedPosition) {
    return {
      focusedDay: lastFocusedDay,
      isLimitReached: true,
      pendingMoveKey: moveActionKey,
      prevFocusedDayOutOfBounds: false
    };
  }
  const newFocusedDay = monthsData[newFocusedPosition.monthIndex].weeks[newFocusedPosition.weekIndex][newFocusedPosition.dayIndex];
  const isCursorAnEmptyDay = typeof newFocusedDay === 'boolean' && !newFocusedDay;
  if (isCursorAnEmptyDay) {
    // Empty day, keep moving in the same direction until we find a day or it reaches a limit
    // Disabled day, keep moving in the same direction until we find a day or it reaches a limit
    return moveCursor(newFocusedPosition, moveActionKey, monthsData, lastFocusedDay);
  }
  return {
    focusedDay: newFocusedDay,
    isLimitReached: false,
    pendingMoveKey: null,
    prevFocusedDayOutOfBounds: false
  };
};
const dayFocusReducer = (state, action) => {
  const {
    type,
    day = null,
    arrowKey,
    monthsData
  } = action;
  const {
    focusedDay,
    pendingMoveKey,
    isLimitReached,
    prevFocusedDayOutOfBounds
  } = state;
  switch (type) {
    case 'SET_FOCUS_DAY':
      {
        return {
          ...state,
          focusedDay: day
        };
      }
    case 'MOVE_FOCUS_DAY_WITH_ARROWS':
      {
        if (!day || !arrowKey || !monthsData) {
          return state;
        }
        const {
          focusedDay: newFocusedDay,
          isLimitReached: newIsLimitReached,
          pendingMoveKey: newPendingMoveKey
        } = moveCursor(day.position, arrowKey, monthsData, day);
        return {
          ...state,
          focusedDay: newFocusedDay,
          isLimitReached: newIsLimitReached,
          pendingMoveKey: newPendingMoveKey
        };
      }
    /**
     * There are 2 scenarios:
     * - Move cursor that is already rendered
     * - Move cursor that is not rendered in current view (prevFocusedDayOutOfBounds = true)
     */
    case 'MOVE_PENDING_CURSOR':
      {
        if (!isLimitReached || !pendingMoveKey || !focusedDay || !monthsData) {
          return state;
        }
        const position = prevFocusedDayOutOfBounds ? getNewFocusDayPosition(focusedDay, pendingMoveKey, monthsData) : focusedDay.position;
        if (prevFocusedDayOutOfBounds) {
          const {
            monthIndex,
            weekIndex,
            dayIndex
          } = position;
          const nextFocusedDay = monthsData[monthIndex].weeks[weekIndex][dayIndex];
          // If next cursor contains a valid day, just return it
          if (nextFocusedDay) {
            return {
              focusedDay: nextFocusedDay,
              isLimitReached: false,
              pendingMoveKey: null,
              prevFocusedDayOutOfBounds: false
            };
          }
        }
        const {
          focusedDay: newFocusedDay,
          isLimitReached: newLimitReached,
          pendingMoveKey: newPendingOperation
        } = moveCursor(position, pendingMoveKey, monthsData, focusedDay);
        return {
          focusedDay: newFocusedDay,
          isLimitReached: newLimitReached,
          pendingMoveKey: newPendingOperation,
          prevFocusedDayOutOfBounds: false
        };
      }
    /**
     * There are 2 scenarios:
     * - Recalculate focused position cursor that is already rendered
     * - Focused cursor is not rendered anymore, set prevFocusedDayOutOfBounds = true
     */
    case 'REPOSITION_CURRENT_CURSOR_IN_MONTH':
      {
        if (!focusedDay || !monthsData || !pendingMoveKey) {
          return state;
        }
        const newMonthIndex = monthsData.findIndex(monthData => monthData.month === focusedDay.month && monthData.year === focusedDay.year);
        if (newMonthIndex === -1) {
          return {
            ...state,
            prevFocusedDayOutOfBounds: true
          };
        }
        const updatedCursor = {
          ...focusedDay,
          position: {
            ...focusedDay.position,
            monthIndex: newMonthIndex
          }
        };
        return {
          ...state,
          focusedDay: updatedCursor
        };
      }
    default:
      {
        return state;
      }
  }
};
const useDayFocus = (monthsData, onCalendarLimitReached) => {
  const [{
    focusedDay,
    isLimitReached,
    pendingMoveKey
  }, dispatch] = React.useReducer(dayFocusReducer, initialState);
  React.useEffect(() => {
    if (isLimitReached && pendingMoveKey && onCalendarLimitReached) {
      onCalendarLimitReached(pendingMoveKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLimitReached, pendingMoveKey]);
  React.useEffect(() => {
    dispatch({
      type: 'REPOSITION_CURRENT_CURSOR_IN_MONTH',
      monthsData
    });
    dispatch({
      type: 'MOVE_PENDING_CURSOR',
      monthsData
    });
  }, [monthsData]);
  const dayFocusActions = React.useMemo(() => ({
    clearFocusedDay: () => dispatch({
      type: 'SET_FOCUS_DAY',
      day: null
    }),
    moveFocusedDayWithArrows: (arrowKey, day) => dispatch({
      type: 'MOVE_FOCUS_DAY_WITH_ARROWS',
      arrowKey,
      day,
      monthsData
    }),
    setFocusedDay: day => dispatch({
      type: 'SET_FOCUS_DAY',
      day
    })
  }), [monthsData]);
  return React.useMemo(() => ({
    focusedDay,
    dayFocusActions
  }), [focusedDay, dayFocusActions]);
};
const getClassNameList$6 = classNames.bind(styles);
const dateMatchesMonth = (date, month, year) => date.getMonth() === month && date.getFullYear() === year;
const isSelectionStartInMonth = (month, year, startDateString) => {
  if (!startDateString) {
    return false;
  }
  const startDate = dateUtils.getDateFromString(startDateString);
  return dateMatchesMonth(startDate, month, year);
};
const getRangeSelection = (day, {
  selectionStart,
  selectionEnd,
  selectionChangeMode,
  enableSameDayDateRange
}) => {
  // If there is already a start of selection, but no end
  if (selectionStart && (!selectionEnd || selectionChangeMode === 'end')) {
    const dateCompare = compareDateStrings(selectionStart, day.dateString);
    const datesAreTheSame = dateCompare === 0;
    // Is the incoming date greater than the selectionStart or
    // is the same day selected and same day selection allowed?
    if (dateCompare > 0 || datesAreTheSame && enableSameDayDateRange) {
      return {
        selectionStart,
        selectionEnd: day.dateString,
        focusedDay: day
      };
    }
    if (datesAreTheSame) {
      // If incoming date equals selectionStart cancel out the selection
      return {
        selectionStart: null,
        selectionEnd: null,
        focusedDay: day // Keep day focused for keyboard interaction
      };
    }
    return {
      selectionStart: day.dateString,
      selectionEnd: null,
      focusedDay: day
    };
  }
  return {
    selectionStart: day.dateString,
    selectionEnd: null,
    focusedDay: day
  };
};
const EGDSBaseCalendar = props => {
  const {
    className,
    customAriaLabels,
    dataTestId,
    dayData,
    dayNameLength = 'narrow',
    dayTemplateComponent = DefaultDayTemplate,
    enablePastDates,
    enableSameDayDateRange,
    handleShape,
    isSingleDateSelection,
    /** A moniker or BCP-47 style language tag string. */
    locale,
    maxDate,
    minDayCellHeight,
    month,
    monthLabelAlignment,
    numColumns = 1,
    numMonthsToDisplay = 1,
    onArrowNavigation = () => {},
    onDisabledDayClick,
    onHoverDay = () => {},
    onScrollHandler,
    onSelectionChanged = () => {},
    padWeeks,
    preventScrollOnDayFocus,
    scrollToMonth,
    scrollToMonthRef,
    selectable,
    selectionChangeMode,
    selectionEnd,
    selectionStart,
    firstMonthCallback,
    secondMonthCallback,
    lastMonthCallback,
    showGridlines,
    showMonthLabels,
    showSiblingMonths = false,
    skipToButtonRef,
    stretchy = false,
    submitButtonLabel,
    year
  } = props;
  const l10n = useLocalization();
  const monthsData = useCalendarDays({
    numMonthsToDisplay,
    year,
    month,
    showSiblingMonths,
    locale,
    maxDate,
    padWeeks
  });
  const {
    focusedDay,
    dayFocusActions
  } = useDayFocus(monthsData, onArrowNavigation);
  const isTouch = typeof window !== 'undefined' && 'ontouchstart' in window;
  React.useEffect(() => {
    // If dates are cleared, we need to clear the focus state
    if (Object.is(selectionStart, null)) {
      dayFocusActions.clearFocusedDay();
    }
    // Do not add dayFocusActions as a dependency. We only need to check if selectionStart has changed
    // Otherwise focusedDay might be cleared when new monthData is fetched
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionStart]);
  const handleDayClick = React.useCallback(date => {
    const {
      dateString
    } = date;
    if (!dateString || !selectable) {
      return;
    }
    if (isSingleDateSelection) {
      onSelectionChanged(dateString);
    } else {
      const {
        focusedDay: newFocusDay,
        selectionStart: newSelectionStart,
        selectionEnd: newSelectionEnd
      } = getRangeSelection(date, {
        selectionStart,
        selectionEnd,
        selectionChangeMode,
        enableSameDayDateRange
      });
      if (!isTouch) {
        dayFocusActions.setFocusedDay(newFocusDay);
      }
      // Notify the outside world
      onSelectionChanged(newSelectionStart, newSelectionEnd);
    }
  }, [dayFocusActions, enableSameDayDateRange, isSingleDateSelection, isTouch, onSelectionChanged, selectable, selectionChangeMode, selectionEnd, selectionStart]);
  const handleUnconfirmedSelectionHover = React.useMemo(() => debounce(35, date => {
    const {
      dateString
    } = date;
    if (selectionStart && !selectionEnd && selectable) {
      const isValidPreselectionHoveredDate = isLaterDate(selectionStart, dateString) || enableSameDayDateRange && isSameDate(selectionStart, dateString);
      if (isSingleDateSelection) {
        onHoverDay(dateString);
      } else if (isValidPreselectionHoveredDate) {
        // We change focus to reset and sync any focus done previously with a keyboard
        dayFocusActions.setFocusedDay(date);
        onHoverDay(dateString);
      }
    }
  }), [dayFocusActions, enableSameDayDateRange, isSingleDateSelection, onHoverDay, selectable, selectionEnd, selectionStart]);
  const handleDayKeyboardEvents = React.useCallback((e, day) => {
    if (!['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    dayFocusActions.moveFocusedDayWithArrows(e.key, day);
  }, [dayFocusActions]);
  /**
   *
   * @param buttonRef
   * @returns a skip to submit* button that is invisible to non-google-talkback accessibility tool users. More information found here https://github.expedia.biz/egds/uitk-react/pull/5300#issuecomment-1885084
   */
  const skipToButton = buttonRef => {
    if (!buttonRef) {
      return;
    }
    const onClick = () => {
      if (buttonRef && buttonRef.current) {
        buttonRef.current.focus();
      }
    };
    return jsx("button", {
      className: getClassNameList$6('is-visually-hidden', 'uitk-calendar-skip-to-button'),
      onClick: onClick,
      tabIndex: -1,
      type: "button",
      children: submitButtonLabel ? l10n.formatText('calendar.skipToButton', submitButtonLabel) : l10n.formatText('calendar.default.skipToButton')
    });
  };
  const calendarClasses = getClassNameList$6(className, 'uitk-calendar', {
    'uitk-calendar-day-selection-rectangle': handleShape === 'rectangle',
    'uitk-calendar-day-selection-circle': handleShape === 'circle',
    'uitk-no-gridlines': !showGridlines,
    'uitk-calendar-stretchy': stretchy
  });
  const calendarProps = {
    className: calendarClasses,
    ...(dataTestId && {
      'data-stid': `${dataTestId}-calendar`
    })
  };
  const unconfirmedSelectionEnd = selectionStart && !selectionEnd && focusedDay ? focusedDay.dateString : null;
  const todaysDate = new Date();
  return jsxs("div", {
    ...calendarProps,
    role: "application",
    children: [jsx(AriaLive$1, {
      customAriaLabels,
      selectionStart,
      selectionEnd,
      locale
    }), monthsData.map((monthData, index) => {
      // If this is the current month or the month contains the selected dates, then scroll into view
      const shouldScrollIntoView = !!scrollToMonth && (!selectionStart && dateMatchesMonth(todaysDate, monthData.month, monthData.year) || isSelectionStartInMonth(monthData.month, monthData.year, selectionStart));
      const isFirstMonth = index === 0;
      const isSecondMonth = index === 1;
      const isLastMonth = index === monthsData.length - 1;
      return jsxs(Fragment, {
        children: [jsx(EGDSMonth$1, {
          index,
          year,
          customAriaLabels,
          dayData,
          dayNameLength,
          dayTemplateComponent,
          enablePastDates,
          enableSameDayDateRange,
          focusedDay,
          handleArrowKeyPress: handleDayKeyboardEvents,
          isFirstMonth,
          isLastMonth,
          isSecondMonth,
          isSingleDateSelection,
          locale,
          l10n,
          maxDate,
          minDayCellHeight,
          monthLabelAlignment,
          monthData,
          numColumns,
          numMonthsToDisplay,
          onDayClick: handleDayClick,
          onDayHover: handleUnconfirmedSelectionHover,
          onDisabledDayClick,
          onScrollHandler,
          padWeeks,
          preventScrollOnDayFocus,
          scrollToMonth,
          scrollToMonthRef,
          selectable,
          selectionEnd,
          selectionStart,
          firstMonthCallback,
          secondMonthCallback,
          lastMonthCallback,
          shouldScrollIntoView,
          showMonthLabels,
          showSiblingMonths,
          stretchy,
          unconfirmedSelectionEnd
        }, `month-${monthData.year}-${monthData.month}`), skipToButton && skipToButton(skipToButtonRef)]
      });
    })]
  });
};
EGDSBaseCalendar.displayName = 'EGDSBaseCalendar';
EGDSBaseCalendar.defaultProps = {
  dayData: {
    defaults: {}
  },
  dayTemplateComponent: DefaultDayTemplate,
  enablePastDates: false,
  enableSameDayDateRange: false,
  handleShape: 'rectangle',
  monthLabelAlignment: 'center',
  onDisabledDayClick: () => {},
  padWeeks: false,
  selectable: false,
  selectionChangeMode: 'start',
  selectionEnd: null,
  selectionStart: null,
  showGridlines: false,
  showMonthLabels: true
};
const getClassNameList$5 = classNames.bind(styles);
const EGDSCalendarNavigationControls = ({
  canAdvance,
  canRetreat,
  children,
  className,
  enabled,
  navType,
  numMonths = 0,
  stretchy = false,
  handleNextClicked,
  handlePrevClicked
}) => {
  const isInset = navType === 'inset';
  const rootClasses = getClassNameList$5({
    'uitk-cal-controls': true,
    'uitk-cal-controls-stretchy': stretchy,
    className
  });
  const l10n = useLocalization();
  const parentClasses = getClassNameList$5({
    'uitk-cal-controls-parent-inset-single': isInset && numMonths === 1,
    'uitk-cal-controls-parent-outset-single': !isInset && numMonths === 1,
    'uitk-cal-controls-parent-outset-multi': !isInset && numMonths > 1,
    'uitk-cal-controls-parent-stretchy': stretchy
  });
  const buttonClasses = getClassNameList$5('uitk-cal-controls-button', {
    'uitk-cal-controls-button-inset': isInset && numMonths === 1,
    'uitk-cal-controls-button-inset-multi': isInset && numMonths > 1,
    'uitk-cal-controls-button-outset': !isInset
  });
  const prevButtonClasses = getClassNameList$5(buttonClasses, 'uitk-cal-controls-button-prev');
  const nextButtonClasses = getClassNameList$5(buttonClasses, 'uitk-cal-controls-button-next');
  const previousUID = Math.random().toString(36).substring(7);
  const nextUID = Math.random().toString(36).substring(7);
  if (enabled) {
    return jsxs("div", {
      className: rootClasses,
      children: [jsx("div", {
        className: prevButtonClasses,
        children: jsx(EGDSPagingPrevButton, {
          "data-stid": "uitk-calendar-navigation-controls-previous-button",
          disabled: !canRetreat,
          iconId: previousUID,
          iconTitle: l10n.formatText('calendar.prevMonthButton.a11yLabel'),
          onClick: handlePrevClicked
        })
      }), jsx("div", {
        className: nextButtonClasses,
        children: jsx(EGDSPagingNextButton, {
          "data-stid": "uitk-calendar-navigation-controls-next-button",
          disabled: !canAdvance,
          iconId: nextUID,
          iconTitle: l10n.formatText('calendar.nextMonthButton.a11yLabel'),
          onClick: handleNextClicked
        })
      }), jsx("div", {
        className: parentClasses,
        children: children
      })]
    });
  }
  const childrenClassNames = getClassNameList$5(className, 'uitk-cal-controls-empty');
  EGDSCalendarNavigationControls.displayName = 'EGDSCalendarNavigationControls';
  return jsx("div", {
    className: childrenClassNames,
    children: children
  });
};

/**
 * Returns a string for Voiceover
 * Currently it supports 3 cases
 * 1. Single date - "your current month is May 2023"
 * 2. Two consecutive months - "your current months are May 2023 and June 2023"
 * 3. For multiple months - "your current months are between May 2023 and October 2023"
 *
 * @param {string} month
 * @param {string} year
 * @param {string} numMonthsToDisplay
 * @param {ILocalization} l10n - Localization Object
 * @return {string}
 */
const getAriaLiveLabel = (month, year, numMonthsToDisplay, l10n) => {
  const startMonth = new Date(year, month, 1, 12);
  let monthArray = [startMonth.toLocaleString('default', {
    month: 'long'
  }), year];
  if (numMonthsToDisplay === 1) {
    return l10n.formatText('calendar.ariaLabelSingleMonthChange', monthArray);
  }
  const endMonth = new Date(year, month + numMonthsToDisplay - 1, 1, 12);
  monthArray = [...monthArray, endMonth.toLocaleString('default', {
    month: 'long'
  }), endMonth.toLocaleString('default', {
    year: 'numeric'
  })];
  if (numMonthsToDisplay === 2) {
    return l10n.formatText('calendar.ariaLabelMonthChange', monthArray) || '';
  }
  return l10n.formatText('calendar.ariaLabelMultiMonthChange', monthArray) || '';
};
const usePrevious = value => {
  const ref = React.useRef(null);
  React.useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};
const EGDSCalendarNavigationPagination = props => {
  const {
    className,
    handleShape = 'rectangle',
    isSingleDateSelection,
    locale = 'en-US',
    maxDate = null,
    minDate = new Date(),
    month: monthProp,
    navType = 'outset',
    numMonthsToDisplay = 2,
    onHoverDay,
    onSelectionChanged,
    padWeeks = false,
    selectable,
    selectionChangeMode,
    selectionEnd,
    selectionStart,
    stretchy,
    year: yearProp,
    numColumns,
    numMonthsToAdvance = 1,
    onNextClicked,
    onPrevClicked,
    ...rest
  } = props;
  const l10n = useLocalization();
  const {
    month: defaultMonth,
    year: defaultYear
  } = getMonthYear(selectionStart, null, {
    month: monthProp,
    year: yearProp
  });
  const [month, setMonth] = React.useState(defaultMonth);
  const [year, setYear] = React.useState(defaultYear);
  const prevMonth = usePrevious(monthProp);
  const prevYear = usePrevious(yearProp);
  React.useEffect(() => {
    if (typeof monthProp === 'number' && typeof yearProp === 'number') {
      if (prevMonth !== monthProp || prevYear !== yearProp) {
        setMonth(monthProp);
        setYear(yearProp);
      }
    }
  }, [monthProp, yearProp, prevMonth, prevYear]);
  const ariaLiveLabel = React.useMemo(() => getAriaLiveLabel(month, year, numMonthsToDisplay, l10n), [month, year, numMonthsToDisplay, l10n]);
  const getControlState = (_month, _year) => {
    const isWithinBounds = (boundaryDate, minOrMax) => {
      // Normalize the min/max Date and current Date so we can compare the 1st of each month
      // When advancing, offset the number of months so maxDate stops in last column
      // Once the current Date tries to exceed the min/max Date, return false
      const isMin = minOrMax === 'min';
      const offset = isMin ? 0 : numMonthsToDisplay - 1;
      const monthDate = new Date(_year, _month + offset).getTime();
      const boundaryMonthDate = new Date(boundaryDate.getFullYear(), boundaryDate.getMonth()).getTime();
      return isMin ? monthDate > boundaryMonthDate : monthDate < boundaryMonthDate;
    };
    const _canRetreat = minDate ? isWithinBounds(minDate, 'min') : true;
    const _canAdvance = maxDate ? isWithinBounds(maxDate, 'max') : true;
    return {
      canAdvance: _canAdvance,
      canRetreat: _canRetreat
    };
  };
  const {
    canAdvance: defaultCanAdvance,
    canRetreat: defaultCanRetreat
  } = getControlState(month, year);
  const [canAdvance, setCanAdvance] = React.useState(defaultCanAdvance);
  const [canRetreat, setCanRetreat] = React.useState(defaultCanRetreat);
  const handlePrevClicked = () => {
    let {
      updatedYear,
      updatedMonth
    } = dateUtils.addMonthsToMonth(year, month, -numMonthsToAdvance);
    const {
      canRetreat: _canRetreat,
      canAdvance: _canAdvance
    } = getControlState(updatedMonth, updatedYear);
    // If target date is before min date, set date to minDate
    if (minDate && _canRetreat === false) {
      updatedMonth = minDate.getMonth();
      updatedYear = minDate.getFullYear();
    }
    setMonth(updatedMonth);
    setYear(updatedYear);
    setCanRetreat(_canRetreat);
    setCanAdvance(_canAdvance);
    onPrevClicked && onPrevClicked({
      month: updatedMonth,
      year: updatedYear
    });
  };
  const handleNextClicked = () => {
    let offset = 0;
    if (numMonthsToDisplay) offset = numMonthsToDisplay - 1 || 0;
    let {
      updatedYear,
      updatedMonth
    } = dateUtils.addMonthsToMonth(year, month, numMonthsToAdvance);
    const {
      canRetreat: _canRetreat,
      canAdvance: _canAdvance
    } = getControlState(updatedMonth, updatedYear);
    // If target date is after max date, set date to maxDate
    if (maxDate && _canAdvance === false) {
      updatedMonth = maxDate.getMonth() - offset;
      updatedYear = maxDate.getFullYear();
    }
    setMonth(updatedMonth);
    setYear(updatedYear);
    setCanRetreat(_canRetreat);
    setCanAdvance(_canAdvance);
    onNextClicked && onNextClicked({
      month: updatedMonth,
      year: updatedYear
    });
  };
  // If the user is navigating the calendar days via arrow keys and not using the prev/next buttons,
  // then a callback is triggered within calendar-base to notify us when we need to move to the previous or next month.
  const handleArrowNavigation = direction => {
    // The right/down arrow key was used while on the last day of a last visible month,
    // so we'll fake the next button being click by calling handleNextClicked()
    if (canAdvance && (direction === 'ArrowRight' || direction === 'ArrowDown')) {
      // The left/up arrow key was used while on the first day of the first visible month,
      // so we'll fake a prev button being clicked by calling handlePrevClicked()
      handleNextClicked();
    } else if (canRetreat && (direction === 'ArrowLeft' || direction === 'ArrowUp')) {
      // The up arrow key was used while on the first day of the first visible month,
      // so we'll fake a prev button being clicked by calling handlePrevClicked()
      handlePrevClicked();
    }
  };
  const numMonths = numMonthsToDisplay;
  const numColumnsToDisplay = numColumns || numMonthsToDisplay;
  return jsxs(Fragment, {
    children: [jsx("span", {
      className: "is-visually-hidden",
      "aria-live": "polite",
      children: ariaLiveLabel
    }), jsx(EGDSCalendarNavigationControls, {
      stretchy: stretchy,
      canAdvance: canAdvance,
      canRetreat: canRetreat,
      className: className,
      enabled: true,
      navType: navType,
      numMonths: numMonths,
      handleNextClicked: handleNextClicked,
      handlePrevClicked: handlePrevClicked,
      children: jsx(EGDSBaseCalendar, {
        ...rest,
        stretchy: stretchy,
        handleShape: handleShape,
        isSingleDateSelection: isSingleDateSelection,
        locale: locale,
        maxDate: maxDate ?? undefined,
        month: month,
        numColumns: numColumnsToDisplay,
        numMonthsToDisplay: numMonths,
        onArrowNavigation: handleArrowNavigation,
        onHoverDay: onHoverDay,
        onSelectionChanged: onSelectionChanged,
        padWeeks: padWeeks,
        selectable: selectable,
        selectionChangeMode: selectionChangeMode,
        selectionEnd: selectionEnd,
        selectionStart: selectionStart,
        year: year
      })
    })]
  });
};
EGDSCalendarNavigationPagination.displayName = 'EGDSCalendarNavigationPagination';
const getClassNameList$4 = classNames.bind(styles);
const getClosestScrollableParent = element => {
  let parent = element?.parentElement;
  while (parent) {
    const {
      overflowY
    } = window.getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
};
const getMaxNumNextMonths = (month, year, maxDate) => {
  // Get the maximum number of months to load, including start and end months
  let numMonths;
  const monthDate = new Date(year, month, 1);
  numMonths = (maxDate.getFullYear() - monthDate.getFullYear()) * 12;
  numMonths -= monthDate.getMonth();
  numMonths += maxDate.getMonth() + 1;
  return numMonths <= 0 ? 0 : numMonths;
};
/**
 * This function is used to get the initial number of months to load
 * when the component mounts.
 * This function also handles the case when the selection is in the past or future.
 */
const getInitialPrevNextNumMonths = (monthYear, selectionStart, numMonths, minDate) => {
  const monthDate = new Date(monthYear.year, monthYear.month, 1);
  const selectedDateArr = selectionStart?.split('-');
  const isSelected = selectedDateArr?.length === 3;
  const correctedMinDate = minDate ? new Date(minDate.getFullYear(), minDate.getMonth(), 1) : null;
  let numPrevMonths = correctedMinDate && correctedMinDate < monthDate ? 1 : 0;
  let numNextMonths = numMonths - numPrevMonths;
  if (isSelected) {
    const selectedYear = Number(selectedDateArr[0]);
    const selectedMonth = Number(selectedDateArr[1]) - 1;
    const selDate = new Date(selectedYear, selectedMonth, 1);
    const isSelectionInPastMonth = selDate < monthDate;
    const isSelectionInFutureMonth = selDate > monthDate;
    if (isSelectionInPastMonth) {
      numPrevMonths = monthDate.getMonth() - selDate.getMonth() + 12 * (monthDate.getFullYear() - selDate.getFullYear());
      const oneMorePreviousMonth = correctedMinDate && correctedMinDate < selDate ? 1 : 0;
      return {
        numPrevMonths: numPrevMonths + oneMorePreviousMonth,
        numNextMonths: 1
      };
    }
    if (isSelectionInFutureMonth) {
      numNextMonths = selDate.getMonth() - monthDate.getMonth() + 12 * (selDate.getFullYear() - monthDate.getFullYear()) + 1;
      numPrevMonths = numNextMonths >= numMonths - 1 ? 0 : numPrevMonths;
      return {
        numPrevMonths,
        numNextMonths: numNextMonths < numMonths ? numMonths - numPrevMonths : numNextMonths
      };
    }
  }
  return {
    numPrevMonths,
    numNextMonths
  };
};
const getNumMonths = (month, year) => year * 12 + month + 1;
const getMaxNumPrevMonths = (month, year, minDate = new Date()) => {
  const minDateYear = minDate.getFullYear();
  const minDateMonth = minDate.getMonth();
  const getNumMinMonths = getNumMonths(minDateMonth, minDateYear);
  const getNumCurrentMonths = getNumMonths(month, year);
  const monthDiff = getNumCurrentMonths - getNumMinMonths;
  return monthDiff < 0 ? 0 : monthDiff;
};
const deleteMonthsFromMonthDate = (year, month, numMonths) => {
  const numDateMonths = getNumMonths(month, year);
  const diffNumMonths = numDateMonths - numMonths;
  const updatedYear = Math.floor(diffNumMonths / 12);
  const updatedMonth = diffNumMonths % 12 - 1;
  return {
    updatedYear,
    updatedMonth
  };
};
const EGDSCalendarNavigationScrolling = props => {
  const elem = React.useRef(null);
  const {
    month,
    year
  } = getMonthYear();
  const {
    stretchy,
    selectionStart,
    numMonthsToDisplay = 4,
    maxDate,
    minDate,
    padWeeks = false
  } = props;
  const [{
    numPrevMonths,
    numNextMonths
  }, setNumPrevNextMonths] = React.useState(() => getInitialPrevNextNumMonths({
    month,
    year
  }, selectionStart,
  // We need atleast 4 months initially loaded to ensure
  // a scroll overflow is created to enable infinite scroll
  numMonthsToDisplay > 4 ? numMonthsToDisplay : 4, minDate));
  const maxLoadableNextMonths = maxDate ? getMaxNumNextMonths(month, year, maxDate) : null;
  const maxLoadablePrevMonths = getMaxNumPrevMonths(month, year, minDate);
  const {
    className,
    locale,
    ...rest
  } = props;
  const rootClassNames = getClassNameList$4('uitk-calendar-navigation-scrolling', className);
  const {
    updatedYear: startYear,
    updatedMonth: startMonth
  } = deleteMonthsFromMonthDate(year, month, numPrevMonths);
  const numColumns = 1;
  const firstMonthRef = React.useRef(null);
  const secondMonthRef = React.useRef(null);
  const lastMonthRef = React.useRef(null);
  const observerRef = React.useRef(null);
  const currentMonth = React.useRef(null);
  const closestScrollableParent = React.useRef(null);
  // This ref is used to prevent scrolling to the current month when the component renders.
  const preventScrollOnDayFocus = React.useRef(false);
  // This effect is used to scroll to the current month when the component mounts.
  React.useEffect(() => {
    currentMonth.current?.scrollIntoView?.();
  }, []);
  // This callback is used to update the observer when the first month ref is set.
  const handleFirstMonthChange = React.useCallback(node => {
    if (firstMonthRef.current) observerRef.current?.unobserve(firstMonthRef.current);
    firstMonthRef.current = node;
    observerRef.current?.observe(firstMonthRef.current);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [firstMonthRef.current]);
  const handleSecondMonthChange = React.useCallback(node => {
    secondMonthRef.current = node;
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [secondMonthRef.current]);
  // This callback is used to update the observer when the last month ref is set.
  const handleLastMonthChange = React.useCallback(node => {
    if (lastMonthRef.current) observerRef.current?.unobserve(lastMonthRef.current);
    lastMonthRef.current = node;
    observerRef.current?.observe(lastMonthRef.current);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [lastMonthRef.current]);
  /**
   * This effect is used to update the closest scrollable parent
   * when the element ref is set.
   */
  React.useEffect(() => {
    if (!closestScrollableParent.current && elem.current) closestScrollableParent.current = getClosestScrollableParent(elem.current);
  }, []);
  /**
   * This effect is used to update the number of months to load
   * when the user scrolls to the first or last month.
   */
  React.useEffect(() => {
    if (!observerRef.current && closestScrollableParent.current) {
      observerRef.current = new IntersectionObserver(entries => {
        entries.forEach(({
          target,
          isIntersecting
        }) => {
          if (target === lastMonthRef.current && isIntersecting) {
            /**
             * If the last month is intersecting, then we need to load
             * one more month in the future.
             */
            setNumPrevNextMonths(prevNumPrevNextMonths => {
              if (maxLoadableNextMonths === null || prevNumPrevNextMonths.numNextMonths < maxLoadableNextMonths) {
                return {
                  numPrevMonths: prevNumPrevNextMonths.numPrevMonths,
                  numNextMonths: prevNumPrevNextMonths.numNextMonths + 1
                };
              }
              return {
                numPrevMonths: prevNumPrevNextMonths.numPrevMonths,
                numNextMonths: prevNumPrevNextMonths.numNextMonths
              };
            });
          } else if (target === firstMonthRef.current && isIntersecting) {
            /**
             * If the first month is intersecting, then we need to load
             * one more month in the past.
             */
            setNumPrevNextMonths(prevNumPrevNextMonths => {
              if (prevNumPrevNextMonths.numPrevMonths < maxLoadablePrevMonths) {
                preventScrollOnDayFocus.current = true;
                /**
                 * We need to scroll to the second month in the list
                 * to prevent infinite loop of loading months.
                 */
                secondMonthRef.current?.scrollIntoView();
                return {
                  numPrevMonths: prevNumPrevNextMonths.numPrevMonths + 1,
                  numNextMonths: prevNumPrevNextMonths.numNextMonths
                };
              }
              return {
                numPrevMonths: prevNumPrevNextMonths.numPrevMonths,
                numNextMonths: prevNumPrevNextMonths.numNextMonths
              };
            });
          }
        });
      }, {
        threshold: 0.8,
        root: closestScrollableParent.current,
        rootMargin: '10% 0px 20% 0px'
      });
      // Observe the first and last month
      if (firstMonthRef.current && lastMonthRef.current) {
        observerRef.current.observe(firstMonthRef.current);
        observerRef.current.observe(lastMonthRef.current);
      }
    }
    return () => {
      // Cleanup observer on unmount
      if (observerRef.current) {
        if (firstMonthRef?.current) {
          observerRef.current.unobserve(firstMonthRef.current);
        }
        if (lastMonthRef?.current) {
          observerRef.current.unobserve(lastMonthRef.current);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [observerRef, closestScrollableParent]);
  return jsx("div", {
    className: rootClassNames,
    ref: elem,
    "data-testid": "calendar-scrolling",
    children: jsx(EGDSBaseCalendar, {
      ...rest,
      stretchy: stretchy,
      locale: locale,
      maxDate: maxDate,
      month: startMonth,
      numColumns: numColumns,
      numMonthsToDisplay: numPrevMonths + numNextMonths,
      padWeeks: padWeeks,
      year: startYear,
      scrollToMonth: true,
      scrollToMonthRef: currentMonth,
      firstMonthCallback: handleFirstMonthChange,
      secondMonthCallback: handleSecondMonthChange,
      lastMonthCallback: handleLastMonthChange,
      preventScrollOnDayFocus: preventScrollOnDayFocus
    })
  });
};
const EGDSCalendarNavigation = props => {
  const {
    navType,
    onScrollHandler,
    ...rest
  } = props;
  EGDSCalendarNavigation.displayName = 'EGDSCalendarNavigation';
  if (navType === 'scrolling') {
    return jsx(EGDSCalendarNavigationScrolling, {
      navType: "scrolling",
      onScrollHandler: onScrollHandler,
      ...rest
    });
  }
  return jsx(EGDSCalendarNavigationPagination, {
    ...rest,
    navType: navType
  });
};
const getClassNameList$3 = classNames.bind(styles);
/**
 * Availability backgrounds indicate availability for an entire day or half days to indicate check-in and check-out limitations.
 * Availability background is used as a background layer within day cells.
 * It should stretch to fill the entire area of its cell, sitting on a layer below all other day template content.
 */
const EGDSAvailabilityBackground = props => {
  const {
    children,
    className,
    dataTestId,
    availability
  } = props;
  const wrapperClassNames = getClassNameList$3('uitk-availability-background', EGDSAvailabilityBackgroundAvailabilities.get(availability), className);
  return jsx("div", {
    className: wrapperClassNames,
    "data-test-id": dataTestId,
    children: children
  });
};
EGDSAvailabilityBackground.displayName = 'EGDSAvailabilityBackground';
const getClassNameList$2 = classNames.bind(styles);
const EGDSBarStyle = props => {
  const {
    theme = 'default',
    type = 'solid',
    segment = 'start-end',
    children
  } = props;
  const wrapperClassNames = getClassNameList$2('uitk-event-bar-item', `uitk-event-bar-item-segment-${segment}`, EGDSEventBarThemes.get(theme), EGDSEventBarTypes.get(type));
  return jsx("div", {
    className: wrapperClassNames,
    children: children
  });
};
EGDSBarStyle.displayName = 'EGDSBarStyle';
const getClassNameList$1 = classNames.bind(styles);
const EGDSEventBar = props => {
  const {
    size = 'medium',
    eventItems = [],
    splitDay = false,
    icon
  } = props;
  const wrapperClassNames = getClassNameList$1('uitk-event-bar', `uitk-event-bar-size-${size}`, {
    'uitk-event-bar-split-day': splitDay,
    'uitk-event-bar-has-icon': icon
  });
  return jsx("div", {
    className: wrapperClassNames,
    children: eventItems.map((eventItem, index) => jsx(EGDSBarStyle, {
      ...eventItem,
      children: size !== 'small' && icon && jsx("div", {
        className: `uitk-event-bar-${eventItem.theme}-icon-background`,
        children: icon
      })
    }, index))
  });
};
EGDSEventBar.displayName = 'EGDSEventBar';
const getClassNameList = classNames.bind(styles);
const EGDSLegendSwatches = props => {
  const {
    size,
    barStyle
  } = props;
  const wrapperClassNames = getClassNameList('uitk-legend-swatches', `uitk-legend-swatches-size-${size}`);
  return jsx("div", {
    className: wrapperClassNames,
    children: jsx(EGDSBarStyle, {
      ...barStyle
    })
  });
};
EGDSLegendSwatches.displayName = 'EGDSLegendSwatches';
export { EGDSAvailabilityBackground as E, EGDSAvailabilityBackgroundAvailabilities as a, EGDSBaseCalendar as b, EGDSCalendarNavigation as c, EGDSDateNumber as d, EGDSDateNumberThemes as e, dateUtils$1 as f, EGDSEventBar as g, EGDSBarStyle as h, EGDSLegendSwatches as i, EGDSTypeSizes as j, EGDSTypeWeights as k, EGDSEventBarThemes as l, EGDSEventBarTypes as m, getLocaleDateString as n, getMonthYear as o };